import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { status } from 'constants/general';
import { DISCOUNT_LIST_PAGE_SIZE } from '../constants';
import { fetchOffersProducts } from '../actions';
import type { DiscountItem } from '../types/discount.types';
import { hasMorePages } from '../utils/discountHelpers';
import { useDiscountFilters } from './useDiscountFilters';

export function useOffersList(pageSize = DISCOUNT_LIST_PAGE_SIZE) {
  const dispatch = useDispatch();
  const offersProducts = useSelector((state: any) => state.pharmacyDiscounts.offersProducts);
  const offersProductsLoadStatus = useSelector(
    (state: any) => state.pharmacyDiscounts.offersProductsLoadStatus,
  );

  const {
    stockFilter,
    statusFilter,
    itemTypeFilter,
    advancedFilters,
    filters,
    setStockFilter,
    setStatusFilter,
    setItemTypeFilter,
    setAdvancedFilters,
  } = useDiscountFilters(pageSize);

  const [allItems, setAllItems] = useState<DiscountItem[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const isOffersLoadMoreRef = useRef(false);

  const isLoading = offersProductsLoadStatus === status.FETCHING && !isOffersLoadMoreRef.current;
  const isLoadingMore = offersProductsLoadStatus === status.FETCHING && isOffersLoadMoreRef.current;
  const isError = offersProductsLoadStatus === status.FAIL;
  const hasMore = offersProducts ? hasMorePages(offersProducts.items.length, pageSize) : false;

  useEffect(() => {
    setPageNumber(1);
    setAllItems([]);
    isOffersLoadMoreRef.current = false;
    dispatch(fetchOffersProducts({ ...filters, pageNumber: 1, pageSize }));
  }, [filters, dispatch, pageSize]);

  useEffect(() => {
    if (offersProductsLoadStatus !== status.SUCCESS || !offersProducts) return;
    if (isOffersLoadMoreRef.current) {
      setAllItems(prev => [...prev, ...offersProducts.items]);
      isOffersLoadMoreRef.current = false;
    } else {
      setAllItems(offersProducts.items);
    }
  }, [offersProducts, offersProductsLoadStatus]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    isOffersLoadMoreRef.current = true;
    dispatch(fetchOffersProducts({ ...filters, pageNumber: nextPage, pageSize }));
  }, [hasMore, isLoadingMore, pageNumber, filters, pageSize, dispatch]);

  const reload = useCallback(() => {
    setPageNumber(1);
    setAllItems([]);
    isOffersLoadMoreRef.current = false;
    dispatch(fetchOffersProducts({ ...filters, pageNumber: 1, pageSize }));
  }, [filters, pageSize, dispatch]);

  return {
    stockFilter,
    statusFilter,
    itemTypeFilter,
    advancedFilters,
    filters,
    setStockFilter,
    setStatusFilter,
    setItemTypeFilter,
    setAdvancedFilters,
    discounts: allItems,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reload,
  };
}
